<template>
  <main>
    <define-product-details-accordions>
      <div class="mt-4 <lg:mb-4 lg:mt-10 lg:px-6">
        <slot name="accordions" />
        <slot name="espot-below-accordions" />
      </div>
    </define-product-details-accordions>

    <define-product-details-bottom>
      <slot name="shop-the-look" />
      <slot name="espot-lazy-above-footer" />
      <slot v-if="!product?.dummyCustoms" name="reviews" />
    </define-product-details-bottom>

    <div data-scroll-el="pdpContainer" />
    <div :class="{ 'fit-finder-container': $feature.showFitFinderOnPDP }">
      <slot name="notifications" />
      <transition name="fade">
        <div v-if="productPending && !product">
          <pdp-skeleton />
        </div>
        <div v-else-if="product">
          <div class="lg:mb-10 lg:flex lg:items-start">
            <section
              v-style:p="{ sm: `0 0 ${pxToRem(drawerMinHeight)} 0`, lg: '0' }"
              class="lg:grow"
              :class="{
                '<lg:pointer-events-none': drawerRef?.expanded,
                '<lg:h-screen <lg:overflow-y-auto': !$feature.enablePageScrollBehindPdpDrawer,
              }"
              data-scroll-el="gallery"
            >
              <slot name="gallery" />
              <product-details-accordions class="<lg:hidden" />
            </section>
            <vf-drawer ref="drawerRef" v-style:w="{ lg: '25.5rem' }" class="lg:sticky lg:top-0 lg:shrink-0 lg:px-4 lg:pt-6" :min-height="drawerMinHeight">
              <pdp-section-heading class="mb-2" :is-drawer-expanded="drawerRef?.expanded" />
              <pdp-section-variant-selection :show-only-first-selector-on-mobile="drawerRef?.isCollapsed" />
              <div class="transition-opacity" :class="{ '<lg:op-0': drawerRef?.isCollapsed }">
                <slot name="size-and-fit" />
                <slot name="klarna-payments" />
                <slot name="ctas" />
                <slot name="shipping-methods" />
                <div class="lg:hidden">
                  <product-details-accordions />
                  <product-details-bottom />
                </div>
                <vf-footer v-if="hideDefaultFooter" class="mt-a -mx-4" />
              </div>
            </vf-drawer>
          </div>
          <div class="<lg:hidden">
            <product-details-bottom />
          </div>
        </div>
      </transition>
      <slot name="recommendations" />
    </div>
    <client-only>
      <teleport to="body">
        <slot name="body" />
      </teleport>
    </client-only>
    <slot />
  </main>
</template>

<script setup lang="ts">
defineSlots<{
  'accordions': void
  'body': void
  'ctas': void
  'espot-below-accordions': void
  'espot-lazy-above-footer': void
  'gallery': void
  'heading': void
  'klarna-payments': void
  'notifications': void
  'recommendations': void
  'reviews': void
  'shipping-methods': void
  'shop-the-look': void
  'size-and-fit': void
  'skeletons': void
  'variant-selection': void
}>()

const [DefineProductDetailsAccordions, ProductDetailsAccordions] = createReusableTemplate()
const [DefineProductDetailsBottom, ProductDetailsBottom] = createReusableTemplate()

const { $gtm, $viewport } = useNuxtApp()
const { pending, product, productPending } = usePdp()
const hideDefaultFooter = useState('hideFooter')

const drawerRef = ref()
const drawerMinHeight = 280

watch(pending, (pending) => {
  if (!pending) {
    drawerRef.value.expanded && scrollToElement('gallery', { behavior: 'instant', preventFocus: true })
    drawerRef.value.collapse()
  }
})

watch(() => drawerRef.value?.expanded, (expanded, value) => {
  // Check if value is undefined to prevent firing event when initialized
  if (!product.value || value === undefined) return

  $gtm.push('pdpPage.onDrawerInteraction', product.value.id, expanded)
})

onMounted(() => {
  watch(() => $viewport.lg, () => {
    hideDefaultFooter.value = !$viewport.lg
  }, { immediate: true })
})

onBeforeUnmount(() => {
  hideDefaultFooter.value = false
})
</script>
